import { Badge } from '@/shared/components/ui/badge'
import { cn } from '@/shared/lib/utils'
import { IconProps, Skeleton } from '@mui/material'
import React, { ComponentProps, Suspense } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { CATEGORIES_ICONS } from './CategoryTags.constants'
import { CategoryTagsSkeleton } from './CategoryTagsSkeleton'
import { CategoryTagsProps } from './types'

export const CategoryTags: React.FC<CategoryTagsProps> = ({
  isLoading,
  categories,
  selectedCategory,
}) => {
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const buildHrefForCategory = (id?: string) => {
    const params = new URLSearchParams(searchParams)
    if (!id) {
      params.delete('category')
    } else {
      params.set('category', id)
    }
    const qs = params.toString()
    return qs ? `${location.pathname}?${qs}` : location.pathname
  }

  if (isLoading) {
    return <CategoryTagsSkeleton />
  }

  if (!Array.isArray(categories) || categories.length === 0) {
    return null
  }

  return (
    <div className="flex w-full flex-col items-center justify-center rounded-[20px_20px_0px_0px] px-4 pb-6 pt-8 max-md:max-w-full sm:px-6 sm:pb-8 sm:pt-12 md:px-8">
      <div className="flex max-w-full flex-col items-center font-medium text-mydra-gray">
        <div className="w-full whitespace-nowrap text-sm leading-none tracking-[-0.24px]">
          <div className="flex w-full flex-col items-center max-md:max-w-full">
            <div className="flex flex-wrap justify-center gap-2 max-md:max-w-full">
              <Badge
                key="__all__"
                className={`rounded-[40px] p-0 text-sm font-medium transition-colors ${
                  !selectedCategory
                    ? 'border-primary bg-primary text-white'
                    : 'border border-solid text-mydra-gray hover:bg-gray-50'
                }`}
                variant="outline"
              >
                <Link
                  to={buildHrefForCategory(undefined)}
                  className="flex items-center gap-1 px-2.5 py-2"
                >
                  <CategoryIcon
                    categoryId="other"
                    className={cn(
                      'text-mydra-gray',
                      !selectedCategory ? 'text-white' : 'text-mydra-gray'
                    )}
                  />
                  All
                </Link>
              </Badge>

              {categories.map((category) => {
                const isSelected = selectedCategory?._id === category._id
                return (
                  <Badge
                    key={category._id}
                    className={`rounded-[40px] p-0 text-sm font-medium transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary text-white'
                        : 'border border-solid text-mydra-gray hover:bg-gray-50'
                    }`}
                    variant="outline"
                    data-category-id={category._id}
                  >
                    <Link
                      to={buildHrefForCategory(category._id)}
                      className="flex items-center gap-1 px-2.5 py-2"
                    >
                      <CategoryIcon
                        categoryId={category._id}
                        className={cn(
                          'text-mydra-gray',
                          isSelected ? 'text-white' : 'text-mydra-gray'
                        )}
                      />
                      {category.name}
                    </Link>
                  </Badge>
                )
              })}
            </div>
          </div>
        </div>
        <p className="mt-6 text-center text-xs leading-none text-mydra-gray max-md:max-w-full">
          {selectedCategory?.description ??
            'Transform business problems into AI-powered victories with battle-tested prompts and workflows'}
        </p>
      </div>
    </div>
  )
}

type CategoryIconProps = ComponentProps<IconProps['component']> & {
  categoryId: string
}

function CategoryIcon({ categoryId, ...props }: CategoryIconProps) {
  const Icon = CATEGORIES_ICONS[categoryId] ?? CATEGORIES_ICONS.other

  return (
    <Suspense fallback={<Skeleton {...props} className="size-4" />}>
      <Icon {...props} fontSize="16" />
    </Suspense>
  )
}
